import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

const COLORS = {
  primary:       "#1E3A8A",
  primaryLight:  "#EEF2FF",
  success:       "#16A34A",
  error:         "#DC2626",
  text:          "#0A0F2C",
  secondary:     "#475569",
  border:        "#E2E8F0",
};

const RESEND_COOLDOWN = 30; // seconds
const OTP_LENGTH      = 6;

export default function VerifyEmailOtp() {
  const navigate              = useNavigate();
  const { verifyEmailOtp, resendEmailOtp } = useAuth();

  // Read state passed from login flow
  const tempToken    = sessionStorage.getItem("otp_temp_token")   || "";
  const maskedEmail  = sessionStorage.getItem("otp_masked_email")  || "your email";
  const returnPath   = sessionStorage.getItem("otp_return_path")  || null;

  const [otp, setOtp]               = useState(["", "", "", "", "", ""]);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [loading, setLoading]       = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer]     = useState(RESEND_COOLDOWN);
  const [canResend, setCanResend]         = useState(false);
  const [attemptsLeft, setAttemptsLeft]   = useState(null);

  // Redirect away if no tempToken (user opened page directly)
  useEffect(() => {
    if (!tempToken) {
      navigate("/login", { replace: true });
    }
  }, [tempToken, navigate]);

  // Countdown for resend button
  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true);
      return;
    }
    const id = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  // Focus first box on mount
  useEffect(() => {
    document.getElementById("otp-0")?.focus();
  }, []);

  // ── OTP input handlers ─────────────────────────────────────────

  function handleOtpChange(index, value) {
    // Accept only a single digit
    const digit = value.replace(/\D/g, "").slice(-1);
    const updated = [...otp];
    updated[index] = digit;
    setOtp(updated);
    setError("");

    if (digit && index < OTP_LENGTH - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
      const updated = [...otp];
      updated[index] = "";
      setOtp(updated);
    } else if (e.key === "ArrowLeft" && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  }

  function handleOtpPaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = [...otp];
    for (let i = 0; i < pasted.length; i++) updated[i] = pasted[i];
    setOtp(updated);
    const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    document.getElementById(`otp-${nextIndex}`)?.focus();
  }

  // ── Verify OTP ─────────────────────────────────────────────────

  const handleVerify = useCallback(async () => {
    const otpString = otp.join("");
    if (otpString.length < OTP_LENGTH) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const user = await verifyEmailOtp(tempToken, otpString);

      // Clean up session state
      sessionStorage.removeItem("otp_temp_token");
      sessionStorage.removeItem("otp_masked_email");
      sessionStorage.removeItem("otp_return_path");

      setSuccess("Verification successful! Redirecting…");

      setTimeout(() => {
        if (returnPath) {
          navigate(returnPath, { replace: true });
        } else if (user.role === "super_admin") {
          navigate("/super-admin/dashboard", { replace: true });
        } else if (user.role === "admin") {
          navigate("/admin/dashboard", { replace: true });
        } else {
          navigate("/candidate/dashboard", { replace: true });
        }
      }, 800);

    } catch (err) {
      const msg = err.response?.data?.message || "Invalid OTP. Please try again.";
      setError(msg);
      const left = err.response?.data?.attemptsRemaining;
      if (left != null) setAttemptsLeft(left);
      setOtp(["", "", "", "", "", ""]);
      document.getElementById("otp-0")?.focus();
    } finally {
      setLoading(false);
    }
  }, [otp, tempToken, verifyEmailOtp, navigate, returnPath]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.every((d) => d !== "")) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // ── Resend OTP ─────────────────────────────────────────────────

  async function handleResend() {
    if (!canResend || resendLoading) return;
    setError("");
    setSuccess("");
    setAttemptsLeft(null);
    setResendLoading(true);

    try {
      await resendEmailOtp(tempToken);
      setSuccess("A new OTP has been sent to your email.");
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(RESEND_COOLDOWN);
      setCanResend(false);
      document.getElementById("otp-0")?.focus();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to resend OTP. Please try again.";
      setError(msg);
      const wait = err.response?.data?.waitSeconds;
      if (wait) {
        setResendTimer(wait);
        setCanResend(false);
      }
    } finally {
      setResendLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: `1px solid ${COLORS.border}`,
          borderRadius: 3,
          boxShadow: "0 4px 24px rgba(30,58,138,0.08)",
        }}
      >
        {/* Icon */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: COLORS.primaryLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 36, color: COLORS.primary }} />
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: COLORS.text, mb: 1 }}
          >
            Verify Your Email
          </Typography>

          <Typography variant="body2" sx={{ color: COLORS.secondary, lineHeight: 1.7 }}>
            We sent a 6-digit verification code to
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
              px: 1.5,
              py: 0.5,
              bgcolor: COLORS.primaryLight,
              borderRadius: 2,
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 15, color: COLORS.primary }} />
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: COLORS.primary }}
            >
              {maskedEmail}
            </Typography>
          </Box>
        </Box>

        {/* Alerts */}
        {success && (
          <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {attemptsLeft != null && !error && (
          <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
            {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} remaining before lockout.
          </Alert>
        )}

        {/* OTP Input Boxes */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: 1, sm: 1.5 },
            mb: 3.5,
          }}
          onPaste={handleOtpPaste}
        >
          {otp.map((digit, i) => (
            <Box
              key={i}
              component="input"
              id={`otp-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              autoComplete="one-time-code"
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              disabled={loading || !!success}
              sx={{
                width:  { xs: 44, sm: 52 },
                height: { xs: 52, sm: 60 },
                textAlign: "center",
                fontSize:  { xs: "1.4rem", sm: "1.6rem" },
                fontWeight: 800,
                fontFamily: "'Courier New', Courier, monospace",
                border: `2px solid ${digit ? COLORS.primary : COLORS.border}`,
                borderRadius: "12px",
                outline: "none",
                color:    COLORS.text,
                bgcolor:  digit ? COLORS.primaryLight : "#fff",
                transition: "all 0.15s ease",
                cursor: "text",
                caretColor: COLORS.primary,
                "&:focus": {
                  borderColor: COLORS.primary,
                  bgcolor: COLORS.primaryLight,
                  boxShadow: `0 0 0 3px rgba(30,58,138,0.15)`,
                },
                "&:disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleVerify}
          disabled={loading || !!success || otp.join("").length < OTP_LENGTH}
          sx={{
            bgcolor: COLORS.primary,
            py: 1.5,
            fontWeight: 700,
            fontSize: "1rem",
            borderRadius: 2,
            textTransform: "none",
            mb: 2,
            "&:hover": { bgcolor: "#1E40AF" },
            "&.Mui-disabled": { bgcolor: "#BFDBFE", color: "#fff" },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Verify Code"
          )}
        </Button>

        {/* Resend */}
        <Box sx={{ textAlign: "center", mb: 1 }}>
          {canResend ? (
            <Button
              variant="text"
              startIcon={
                resendLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )
              }
              onClick={handleResend}
              disabled={resendLoading}
              sx={{
                color: COLORS.primary,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.875rem",
              }}
            >
              {resendLoading ? "Sending…" : "Resend Code"}
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: COLORS.secondary }}>
              Resend code in{" "}
              <Box
                component="span"
                sx={{ color: COLORS.primary, fontWeight: 700 }}
              >
                {resendTimer}s
              </Box>
            </Typography>
          )}
        </Box>

        {/* Back to login */}
        <Box sx={{ textAlign: "center", mt: 2, pt: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => {
              sessionStorage.removeItem("otp_temp_token");
              sessionStorage.removeItem("otp_masked_email");
              sessionStorage.removeItem("otp_return_path");
              navigate("/login");
            }}
            sx={{
              color: COLORS.secondary,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.875rem",
            }}
          >
            Back to Login
          </Button>
        </Box>

        {/* Security note */}
        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ color: "#94A3B8", mt: 2, lineHeight: 1.6 }}
        >
          This code expires in 5 minutes. Do not share it with anyone.
          <br />
          This verification is required only once per account.
        </Typography>
      </Paper>
    </Container>
  );
}
